// src/componets/pages/PolicyTabs.jsx
import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FileText, 
  Shield, 
  Database, 
  BookOpen, 
  ChevronLeft,
  Calendar,
  ExternalLink,
  Check
} from "lucide-react";
import Navbar from "../navigation/Navbar";
import policiesContent from "../../data/policiesContent.json";

// Configuración de las pestañas disponibles
const TAB_CONFIG = {
  terms: {
    key: "terms",
    title: "Términos y Condiciones",
    icon: FileText,
    description: "Condiciones de uso del servicio y política de botellones retornables",
    color: "blue"
  },
  privacy: {
    key: "privacy", 
    title: "Política de Privacidad",
    icon: Shield,
    description: "Cómo protegemos y utilizamos tu información personal",
    color: "green"
  },
  data: {
    key: "data",
    title: "Tratamiento de Datos",
    icon: Database,
    description: "Autorización y manejo de datos personales según la ley colombiana",
    color: "purple"
  },
  manual: {
    key: "manual",
    title: "Manual de Usuario", 
    icon: BookOpen,
    description: "Guía completa para usar nuestros servicios de agua purificada",
    color: "orange"
  }
};

// Componente para el header de cada sección
const SectionHeader = ({ title, lastUpdated, color }) => (
  <motion.div
    initial={{ opacity: 0, y: -20 }}
    animate={{ opacity: 1, y: 0 }}
    className="mb-8 pb-6 border-b border-gray-200"
  >
    <h1 className={`text-3xl lg:text-4xl font-medium text-gray-900 mb-2`}>
      {title}
    </h1>
    <div className="flex items-center gap-2 text-sm text-gray-600">
      <Calendar size={16} />
      <span>Última actualización: {lastUpdated}</span>
    </div>
  </motion.div>
);

// Componente para renderizar el contenido de cada sección
const SectionContent = ({ section, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.1 }}
    className="mb-8"
  >
    <h2 className="text-xl lg:text-2xl font-medium text-gray-900 mb-4">
      {section.title}
    </h2>
    <div className="space-y-4">
      {section.content.map((paragraph, paragraphIndex) => (
        <div key={paragraphIndex}>
          {paragraph.includes('**') ? (
            // Renderizar párrafos con formato bold
            <p 
              className="text-gray-700 leading-relaxed"
              dangerouslySetInnerHTML={{
                __html: paragraph
                  .replace(/\*\*(.*?)\*\*/g, '<strong class="font-medium text-gray-900">$1</strong>')
                  .replace(/\n/g, '<br>')
              }}
            />
          ) : (
            <p className="text-gray-700 leading-relaxed">
              {paragraph}
            </p>
          )}
        </div>
      ))}
    </div>
  </motion.div>
);

// Componente para la navegación lateral/superior
const TabNavigation = ({ activeTab, setActiveTab, isMobile }) => {
  if (isMobile) {
    return (
      <div className="mb-6">
        <div className="flex overflow-x-auto gap-2 pb-2 scrollbar-hide">
          {Object.values(TAB_CONFIG).map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-shrink-0 flex items-center gap-2 px-4 py-3 rounded-xl transition-all duration-200 ${
                  isActive 
                    ? `bg-${tab.color}-100 text-${tab.color}-700 border border-${tab.color}-200` 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Icon size={18} />
                <span className="font-medium whitespace-nowrap">{tab.title}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="w-80 pr-8">
      <div className="sticky top-8 space-y-3">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Documentos Legales
        </h3>
        {Object.values(TAB_CONFIG).map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`w-full text-left p-4 rounded-xl transition-all duration-200 group ${
                isActive 
                  ? `bg-${tab.color}-50 border border-${tab.color}-200` 
                  : 'bg-white border border-gray-200 hover:border-gray-300 hover:shadow-sm'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg ${
                  isActive 
                    ? `bg-${tab.color}-100 text-${tab.color}-600` 
                    : 'bg-gray-100 text-gray-600 group-hover:bg-gray-200'
                }`}>
                  <Icon size={20} />
                </div>
                <div className="flex-1">
                  <h4 className={`font-medium mb-1 ${
                    isActive ? `text-${tab.color}-900` : 'text-gray-900'
                  }`}>
                    {tab.title}
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {tab.description}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
        
        {/* Información adicional */}
        <div className="mt-8 p-4 bg-blue-50 rounded-xl border border-blue-200">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Check size={18} className="text-blue-600" />
            </div>
            <div>
              <h4 className="font-medium text-blue-900 mb-1">
                Documentos Actualizados
              </h4>
              <p className="text-sm text-blue-700 leading-relaxed">
                Todos nuestros documentos legales están actualizados conforme a la normatividad colombiana vigente.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Componente principal
const PolicyTabs = ({ activeSection = "terms" }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(activeSection);
  const [isMobile, setIsMobile] = useState(false);

  // Detectar tamaño de pantalla
  useEffect(() => {
    const checkBreakpoint = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkBreakpoint();
    window.addEventListener("resize", checkBreakpoint);
    
    return () => window.removeEventListener("resize", checkBreakpoint);
  }, []);

  // Manejar parámetros de URL
  useEffect(() => {
    const section = searchParams.get('section');
    if (section && TAB_CONFIG[section]) {
      setActiveTab(section);
    }
  }, [searchParams]);

  // Actualizar URL cuando cambia la pestaña
  useEffect(() => {
    const currentUrl = new URL(window.location);
    currentUrl.searchParams.set('section', activeTab);
    window.history.replaceState({}, '', currentUrl);
  }, [activeTab]);

  // Obtener contenido de la pestaña activa
  const getActiveContent = () => {
    return policiesContent[activeTab] || policiesContent.terms;
  };

  const activeContent = getActiveContent();
  const activeTabConfig = TAB_CONFIG[activeTab];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar type="general" staticPosition={true} />
      
      <div className="pt-8 pb-16">
        <div className="max-w-6xl mx-auto px-4 xl:px-0">
          {/* Botón de regreso */}
          <div className="mb-6">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ChevronLeft size={20} />
              <span>Volver</span>
            </button>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Navegación */}
            <TabNavigation 
              activeTab={activeTab} 
              setActiveTab={setActiveTab}
              isMobile={isMobile}
            />

            {/* Contenido principal */}
            <div className="flex-1">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 lg:p-8">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <SectionHeader
                      title={activeContent.title}
                      lastUpdated={activeContent.lastUpdated}
                      color={activeTabConfig.color}
                    />

                    <div className="prose prose-gray max-w-none">
                      {activeContent.sections.map((section, index) => (
                        <SectionContent
                          key={section.id}
                          section={section}
                          index={index}
                        />
                      ))}
                    </div>

                    {/* Footer de la sección */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.5 }}
                      className="mt-12 pt-6 border-t border-gray-200"
                    >
                      <div className="bg-gray-50 rounded-xl p-6">
                        <div className="flex items-start gap-4">
                          <div className="p-3 bg-blue-100 rounded-xl">
                            <ExternalLink size={24} className="text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-medium text-gray-900 mb-2">
                              ¿Necesitas más información?
                            </h3>
                            <p className="text-gray-600 mb-4">
                              Si tienes preguntas sobre estos documentos o necesitas aclarar algún punto, 
                              nuestro equipo está disponible para ayudarte.
                            </p>
                            <button
                              onClick={() => navigate("/")}
                              className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                            >
                              Contactar Soporte
                              <ExternalLink size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CSS personalizado para scrollbar */}
      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        
        /* Fix para colores dinámicos de Tailwind */
        .bg-blue-50 { background-color: rgb(239 246 255); }
        .bg-blue-100 { background-color: rgb(219 234 254); }
        .bg-blue-200 { background-color: rgb(191 219 254); }
        .text-blue-600 { color: rgb(37 99 235); }
        .text-blue-700 { color: rgb(29 78 216); }
        .text-blue-900 { color: rgb(30 58 138); }
        .border-blue-200 { border-color: rgb(191 219 254); }
        
        .bg-green-50 { background-color: rgb(240 253 244); }
        .bg-green-100 { background-color: rgb(220 252 231); }
        .text-green-600 { color: rgb(22 163 74); }
        .text-green-700 { color: rgb(21 128 61); }
        .text-green-900 { color: rgb(20 83 45); }
        .border-green-200 { border-color: rgb(187 247 208); }
        
        .bg-purple-50 { background-color: rgb(250 245 255); }
        .bg-purple-100 { background-color: rgb(243 232 255); }
        .text-purple-600 { color: rgb(147 51 234); }
        .text-purple-700 { color: rgb(126 34 206); }
        .text-purple-900 { color: rgb(88 28 135); }
        .border-purple-200 { border-color: rgb(221 214 254); }
        
        .bg-orange-50 { background-color: rgb(255 247 237); }
        .bg-orange-100 { background-color: rgb(254 215 170); }
        .text-orange-600 { color: rgb(234 88 12); }
        .text-orange-700 { color: rgb(194 65 12); }
        .text-orange-900 { color: rgb(124 45 18); }
        .border-orange-200 { border-color: rgb(254 215 170); }
      `}</style>
    </div>
  );
};

export default PolicyTabs;